import React, { Suspense, lazy } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ApiDocumentationProvider } from './services/DocumentationProvider';

import './i18n';

import { AccountProvider } from './services/AccountProvider';

import DefaultLayout from './layout/DefaultLayout';

import { ProtectedRoutes } from './navigation/ProtectedRoutes';

import NavigateOut from './navigation/NavigateOut';

import './assets/scss/style.scss';
import LoadingBarTop from './components/barTop/barTop';
import cieloLogo from './assets/images/svg/cieloai-new-logo.svg';

const TwoFactorAuthentication = lazy(() =>
  import('./views/pages/ExternalPages/TwoFactorAuthentication')
);
const RequestNewPassword = lazy(() => import('./views/pages/ExternalPages/RequestNewPassword'));
const ResetPassword = lazy(() => import('./views/pages/ExternalPages/ChangePassword'));
const CheckYourEmail = lazy(() => import('./views/pages/ExternalPages/CheckYourEmail'));
const Login = lazy(() => import('./views/pages/ExternalPages/Login'));
const AuthMS = lazy(() => import('./views/pages/ExternalPages/AuthMS'));
const Webrtc = lazy(() => import('./views/pages/ExternalPages/webRTC'));

const SuccessfulPayment = lazy(() => import('./views/pages/ExternalPages/SuccessfulPayment'));
const FailedPayment = lazy(() => import('./views/pages/ExternalPages/FailedPayment'));
const DemoPaymentSuccess = lazy(() => import('./views/pages/ExternalPages/DemoPaymentSuccess'));
const DemoPaymentCancel = lazy(() => import('./views/pages/ExternalPages/DemoPaymentCancel'));
const Payment = lazy(() => import('./views/pages/TwilioTest/Payment'));
const QATest = lazy(() => import('./views/pages/ExternalPages/QATest'));
const WhatsAppOnboarding = lazy(() => import('./views/pages/ExternalPages/WhatsAppOnboarding'));

const ErrorFallback = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#0a0e1a',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      textAlign: 'center',
      padding: '2rem'
    }}
  >
    <img src={cieloLogo} alt="Cielo Logo" style={{ width: '180px', marginBottom: '2rem' }} />
    <h1 style={{ fontSize: '1.8rem', fontWeight: 600, marginBottom: '1rem', color: '#ffffff' }}>
      Something went wrong
    </h1>
    <p
      style={{
        fontSize: '1.1rem',
        color: '#8a8fa8',
        maxWidth: '460px',
        lineHeight: 1.6,
        marginBottom: '2rem'
      }}
    >
      An unexpected error occurred. Our team has already been notified and is working on a fix.
    </p>
    <button
      onClick={() => window.location.replace('/dashboard')}
      style={{
        padding: '0.75rem 2rem',
        fontSize: '1rem',
        fontWeight: 500,
        color: '#ffffff',
        backgroundColor: '#50506C',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
      }}
      onMouseOver={(e) => (e.target.style.backgroundColor = 'rgba(134, 183, 254, 1)')}
      onMouseOut={(e) => (e.target.style.backgroundColor = '#50506C')}
    >
      Go to Dashboard
    </button>
  </div>
);

const App = () => {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <div className="Cielo-bg">
        <ApiDocumentationProvider>
          <AccountProvider>
            <Router>
              <Suspense fallback={<LoadingBarTop />}>
                <Routes>
                  <Route path="/login" exact name="Login" element={<Login />} />
                  <Route path="/saml" exact name="AuthMS" element={<AuthMS />} />
                  <Route
                    path="/2fa"
                    exact
                    name="2FA Verification"
                    element={<TwoFactorAuthentication />}
                  />
                  <Route
                    path="/requestnewpassword"
                    exact
                    name="RequestNewPassword"
                    element={<RequestNewPassword />}
                  />
                  <Route path="/resetpassword" name="ResetPassword" element={<ResetPassword />} />
                  <Route
                    path="/checkyouremail"
                    exact
                    name="CheckYourEmail"
                    element={<CheckYourEmail />}
                  />

                  <Route path="/webrtc/:id" exact name="webrtc" element={<Webrtc />} />

                  <Route path="/payment-success" exact name="Payment" element={<Payment />} />

                  <Route path="/qa-test" exact name="QA Test" element={<QATest />} />

                  <Route
                    path="/whatsapp-onboarding"
                    exact
                    name="WhatsApp Onboarding"
                    element={<WhatsAppOnboarding />}
                  />

                  <Route path="/" element={<ProtectedRoutes />}>
                    <Route path="*" name="Home" element={<DefaultLayout />} />
                  </Route>

                  <Route path="/payment/success/order/:order" element={<SuccessfulPayment />} />
                  <Route path="/payment/failed/order/:type" element={<FailedPayment />} />
                  <Route path="/demo/payment/success" element={<DemoPaymentSuccess />} />
                  <Route path="/demo/payment/cancel" element={<DemoPaymentCancel />} />
                  <Route
                    path="/buy/:stripeSession"
                    element={<NavigateOut url={`${process.env.REACT_APP_CIELO_API_URL}/buy/`} />}
                  />
                </Routes>
              </Suspense>
            </Router>
          </AccountProvider>
        </ApiDocumentationProvider>
      </div>
    </Sentry.ErrorBoundary>
  );
};

export default App;
